import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  EmptyState,
  Icon,
  Tabs,
  useErrorDialog,
  useToast,
} from '@dayon/design-system';
import { formatCount } from '@dayon/money';
import { api, ApiRequestError } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { ScreenHeader } from '../shell/ScreenHeader.tsx';
import './access.css';

import { Roster, type RosterFigures } from './Roster.tsx';
import { PermissionMatrix } from './PermissionMatrix.tsx';
import { SignInTrail } from './SignInTrail.tsx';
import { AdminDrawer } from './AdminDrawer.tsx';
import { InviteDialog } from './InviteDialog.tsx';
import { InviteLinkDialog, type InviteLink } from './InviteLinkDialog.tsx';
import { ActionDialog } from './ActionDialog.tsx';
import { MatrixCellDialog } from './MatrixCellDialog.tsx';
import { ExportDialog } from './ExportDialog.tsx';
import { useAuditExport } from './use-audit-export.ts';
import { NotYetAvailable } from './NotYetAvailable.tsx';
import { stamp } from './format.ts';
import { hasFactor, ACTION_PATH, ACTION_TITLE, MATRIX_REASONS, NOTE_MINIMUM, REASONS } from './vocabulary.ts';
import type {
  ActionKind,
  ActionsPayload,
  AdminRow,
  AttemptRow,
  MatrixPayload,
  PermissionDef,
  RoleDef,
  SessionsPayload,
  TabKey,
} from './types.ts';

/**
 * §3 / §20 Security & access control — the operator identity console.
 *
 * READS, and their capabilities differ:
 *   GET  /api/access/admins               roster                 access.manage
 *   GET  /api/access/matrix               roles × capabilities   access.manage
 *   GET  /api/access/attempts             sign-in trail          audit.export
 *   GET  /api/access/admins/:id/sessions  what a session records access.manage
 *   GET  /api/access/admins/:id/actions   this actor's audit rows audit.export
 *
 * WRITES:
 *   POST /api/access/admins                      invite (a row, not a message)
 *   POST /api/access/admins/:id/invite-link      the single-use acceptance link
 *   POST /api/access/admins/:id/role             change the role they hold
 *   POST /api/access/admins/:id/brand-scope      set the scope, narrowing included
 *   POST /api/access/admins/:id/reset-2fa        clear the factor
 *   POST /api/access/admins/:id/sign-out         end sessions WITHOUT barring
 *   POST /api/access/admins/:id/suspend          bar, and end sessions
 *   POST /api/access/admins/:id/reinstate        lift the bar
 *   POST /api/access/roles/:roleId/permissions   one cell of the matrix
 *   POST /api/access/export                      the audit trail, as CSV
 *
 * THE MATRIX IS A CONTROL, and it is the highest-risk one here: it changes what
 * a CLASS of people may do, at their next request. It reads and writes
 * identity.role_permission, which is the table the session plugin selects a
 * principal's capabilities from on every request — so it was always the guard,
 * and what was missing was a route that could write it. ROLE_MATRIX in
 * @dayon/rbac is the SEED for those rows and nothing more; the CATALOGUE of
 * capabilities stays a compile-time constant, so a permission key that does not
 * exist is still a TypeScript error.
 *
 * WHAT IS AND IS NOT SENT. `@dayon/mailer` records the invite message in the
 * same transaction as the invite token, so the two commit together or not at
 * all. Two things are still true and are still said out loud, because an
 * operator would otherwise assume the opposite:
 *
 *   · Creating an INVITED row sends nothing. There is no link yet to carry.
 *   · A factor reset sends nothing and never will. It transmits no secret; the
 *     admin enrols from their own screen at their next sign-in.
 *
 * And a third, which is CONFIGURATION rather than a property of this console:
 * whether a recorded message is actually delivered depends on MAIL_DRIVER and
 * MAIL_OUTBOUND_MODE. The server answers that per message — the invite-link
 * response carries the outbox's own status for the row it just wrote — and the
 * dialog reports THAT rather than asserting a delivery. The link is shown
 * regardless, because an environment configured to suppress mail must not leave
 * an operator holding nothing.
 *
 * Every write carries a reason CODE from a closed set plus a note, and lands in
 * one transaction with its audit row. Afterwards the roster and the matrix are
 * RE-READ rather than patched: a role change moves the per-role counts, and a
 * suspension moves three compliance tiles as well as the row, so a screen that
 * edited only the row it knew about would end up disagreeing with itself.
 *
 * The rules that close a control are stated where the control is, so the
 * operator meets them before the click rather than after: nobody administers
 * their own authority, and the last Super Admin cannot be demoted or suspended.
 * The route is the guard; these are the warning.
 */

export function AccessControl() {
  const { session, allowed, brandId, brands } = useSession();
  const { showError } = useErrorDialog();
  const { toast } = useToast();

  // The two endpoint permissions differ, so they are asked separately rather
  // than gating the whole module on the stricter one. An Auditor holds
  // audit.export but not access.manage: they get the sign-in trail and a stated
  // wall on the other two tabs — "locked, not hidden".
  const canManage = allowed('access.manage');
  const canExport = allowed('audit.export');

  const [tab, setTab] = useState<TabKey>(() => (canManage ? 'admins' : 'audit'));
  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [admins, setAdmins] = useState<AdminRow[]>([]);
  const [matrix, setMatrix] = useState<MatrixPayload | null>(null);
  const [attempts, setAttempts] = useState<AttemptRow[]>([]);
  const [selected, setSelected] = useState<AdminRow | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invite, setInvite] = useState({
    name: '',
    email: '',
    role: '',
    /** A brand ID, or '' for the absence of a restriction — which is all brands. */
    scope: '',
    twofa: 'Authenticator app',
  });

  /* ── Writing ───────────────────────────────────────────────────────────
     `writeCount` is the read trigger: every successful write increments it and
     the effect below re-reads the roster and the matrix. */
  const [writeCount, setWriteCount] = useState(0);
  const [saving, setSaving] = useState(false);
  const [inviteErrors, setInviteErrors] = useState<Record<string, string>>({});
  const [action, setAction] = useState<ActionKind | null>(null);
  const [actionErrors, setActionErrors] = useState<Record<string, string>>({});
  const [reasonCode, setReasonCode] = useState('');
  const [note, setNote] = useState('');
  const [nextRole, setNextRole] = useState('');
  const [nextBrands, setNextBrands] = useState<string[]>([]);
  const [inviteLink, setInviteLink] = useState<InviteLink | null>(null);

  /* The matrix toggle has its own target, reason and note: it acts on a ROLE
     rather than on an admin, and sharing the member dialog's state would let a
     half-typed suspension note reach a capability grant. */
  const [cell, setCell] = useState<{ role: RoleDef; permission: PermissionDef; grant: boolean } | null>(null);
  const [cellErrors, setCellErrors] = useState<Record<string, string>>({});
  const [cellReason, setCellReason] = useState<string>(MATRIX_REASONS[0]?.code ?? '');
  const [cellNote, setCellNote] = useState('');

  /* The export, and the download of what it produced. Shared with System
     administration through `useAuditExport` — two screens offering the same
     audited act must perform the same act, not two copies of it. */
  const auditExport = useAuditExport({ onRan: () => setWriteCount((n) => n + 1) });

  /*
   * Whether each read FAILED, as distinct from returning nothing.
   *
   * "Nobody is unenrolled" and "we could not ask" are different facts and only
   * one of them is safe for a security lead to act on. One shared flag would be
   * the mirror-image lie: a 503 on the matrix would mark the roster and the
   * trail broken while both were answering perfectly.
   */
  const [rosterFailed, setRosterFailed] = useState(false);
  const [matrixFailed, setMatrixFailed] = useState(false);
  const [attemptsFailed, setAttemptsFailed] = useState(false);
  const [rosterLoading, setRosterLoading] = useState(true);
  const [matrixLoading, setMatrixLoading] = useState(true);
  const [attemptsLoading, setAttemptsLoading] = useState(true);

  /* The drawer's two per-admin reads. They are fetched when a row is opened
     rather than with the roster: a hundred admins is a hundred requests nobody
     asked for, and the panels they back are below the fold. */
  const [sessions, setSessions] = useState<SessionsPayload | null>(null);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsFailed, setSessionsFailed] = useState(false);
  const [actorActions, setActorActions] = useState<ActionsPayload | null>(null);
  const [actionsLoading, setActionsLoading] = useState(false);
  const [actionsFailed, setActionsFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    // No brandId on any of these, deliberately. Identity is platform-wide:
    // admin_brand_scope RESTRICTS an admin, it does not classify one, and no
    // /api/access route accepts the parameter. The mismatch is stated on screen
    // instead — see "Not yet available".
    if (canManage) {
      setRosterLoading(true);
      api
        .get<{ items: AdminRow[] }>('/api/access/admins')
        .then((d) => {
          if (cancelled) return;
          setAdmins(d.items);
          setRosterFailed(false);
          /* The drawer is looking at a row from the PREVIOUS read. Re-point it
             at the same admin in this one, so a suspension the operator just
             made turns the drawer's own button into Reinstate rather than
             leaving them reading the state they changed. */
          setSelected((cur) => (cur ? (d.items.find((a) => a.id === cur.id) ?? null) : null));
        })
        .catch((error: unknown) => {
          if (cancelled) return;
          // Cleared, not left standing: a roster from an earlier read sitting
          // under a failure notice would be read as the current one.
          setAdmins([]);
          setRosterFailed(true);
          showError({
            title: 'Could not load access control',
            message: error instanceof ApiRequestError ? error.message : 'Try again shortly.',
          });
        })
        .finally(() => !cancelled && setRosterLoading(false));

      setMatrixLoading(true);
      api
        .get<MatrixPayload>('/api/access/matrix')
        .then((d) => {
          if (cancelled) return;
          setMatrix(d);
          setMatrixFailed(false);
        })
        .catch((error: unknown) => {
          if (cancelled) return;
          setMatrix(null);
          setMatrixFailed(true);
          showError({
            title: 'Could not load the permission matrix',
            message: error instanceof ApiRequestError ? error.message : 'Try again shortly.',
          });
        })
        .finally(() => !cancelled && setMatrixLoading(false));
    } else {
      // Withheld by capability, which is a third fact again — neither an empty
      // roster nor a failed read. The wall below names it; no flag is set.
      setAdmins([]);
      setRosterFailed(false);
      setRosterLoading(false);
      setMatrix(null);
      setMatrixFailed(false);
      setMatrixLoading(false);
    }

    if (canExport) {
      setAttemptsLoading(true);
      api
        .get<{ items: AttemptRow[] }>('/api/access/attempts')
        .then((d) => {
          if (cancelled) return;
          setAttempts(d.items);
          setAttemptsFailed(false);
        })
        .catch((error: unknown) => {
          if (cancelled) return;
          setAttempts([]);
          setAttemptsFailed(true);
          showError({
            title: 'Could not load the sign-in trail',
            message: error instanceof ApiRequestError ? error.message : 'Try again shortly.',
          });
        })
        .finally(() => !cancelled && setAttemptsLoading(false));
    } else {
      setAttempts([]);
      setAttemptsFailed(false);
      setAttemptsLoading(false);
    }

    return () => {
      cancelled = true;
    };
  }, [canManage, canExport, showError, writeCount]);

  /* The drawer's own reads, keyed on which admin is open and re-run after each
     write — a sign-out changes token_version and appends an audit row, and both
     panels would otherwise describe the account as it was a moment ago. */
  const selectedId = selected?.id ?? null;
  useEffect(() => {
    let cancelled = false;
    if (!selectedId || !canManage) {
      setSessions(null);
      setSessionsFailed(false);
      setSessionsLoading(false);
      return;
    }
    setSessionsLoading(true);
    api
      .get<SessionsPayload>(`/api/access/admins/${selectedId}/sessions`)
      .then((d) => {
        if (cancelled) return;
        setSessions(d);
        setSessionsFailed(false);
      })
      .catch(() => {
        if (cancelled) return;
        // No dialog: the drawer is already open over a roster that loaded, and
        // a modal over a modal for a secondary panel is worse than the panel
        // saying it could not be read. The panel says it.
        setSessions(null);
        setSessionsFailed(true);
      })
      .finally(() => !cancelled && setSessionsLoading(false));
    return () => {
      cancelled = true;
    };
  }, [selectedId, canManage, writeCount]);

  useEffect(() => {
    let cancelled = false;
    if (!selectedId || !canExport) {
      setActorActions(null);
      setActionsFailed(false);
      setActionsLoading(false);
      return;
    }
    setActionsLoading(true);
    api
      .get<ActionsPayload>(`/api/access/admins/${selectedId}/actions`)
      .then((d) => {
        if (cancelled) return;
        setActorActions(d);
        setActionsFailed(false);
      })
      .catch(() => {
        if (cancelled) return;
        setActorActions(null);
        setActionsFailed(true);
      })
      .finally(() => !cancelled && setActionsLoading(false));
    return () => {
      cancelled = true;
    };
  }, [selectedId, canExport, writeCount]);

  /**
   * Compliance arithmetic, all of it from rows on this page.
   *
   * "2FA is mandatory for any role holding funds.approve or promo.publish" is
   * not hardcoded: permission.requiresTwoFactor comes down the wire, so the set
   * widens on its own the day another capability is marked — or the day an
   * operator grants one of them to another role from the grid.
   */
  const figures = useMemo<RosterFigures & { twoFactorRoles: Set<string> }>(() => {
    const twoFactorKeys = new Set(
      (matrix?.permissions ?? []).filter((p) => p.requiresTwoFactor).map((p) => p.key),
    );
    const twoFactorRoles = new Set(
      (matrix?.roles ?? [])
        .filter((r) => r.permissions.some((k) => twoFactorKeys.has(k)))
        .map((r) => r.name),
    );
    const privileged = admins.filter((a) => twoFactorRoles.has(a.role));

    return {
      twoFactorRoles,
      privileged,
      /* The row a security lead is looking for: money capability, and no
         CONFIRMED factor. "Pending setup" counts as unenrolled — an enrolment
         nobody has finished satisfies nothing, and folding it in with App was
         the whole reason the third state exists. */
      unenrolled: privileged.filter((a) => !hasFactor(a.two_factor)),
      active: admins.filter((a) => a.status === 'ACTIVE').length,
      suspended: admins.filter((a) => a.status === 'SUSPENDED').length,
      invited: admins.filter((a) => a.status === 'INVITED').length,
      rolesInUse: new Set(admins.map((a) => a.role)).size,
    };
  }, [admins, matrix]);

  /** "Matches name, email and role (case-insensitive substring)", plus the chip. */
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return admins.filter((a) => {
      if (roleFilter !== 'all' && a.role !== roleFilter) return false;
      if (!q) return true;
      return (
        a.display_name.toLowerCase().includes(q) ||
        a.email.toLowerCase().includes(q) ||
        a.role.toLowerCase().includes(q)
      );
    });
  }, [admins, query, roleFilter]);

  /** The selected admin's own sign-in trail, out of the 100 rows we hold. */
  const history = useMemo(
    () =>
      selected
        ? attempts.filter((a) => a.email_attempted.toLowerCase() === selected.email.toLowerCase())
        : [],
    [attempts, selected],
  );

  /* ── The two rules the screen states before the click ────────────────── */

  /** The signed-in admin is looking at their own row. */
  const isSelf = selected !== null && session !== null && selected.id === session.id;

  /**
   * The one active Super Admin, when there is exactly one.
   *
   * Read from `is_super` on the roster — the role's own flag — rather than from
   * a match on the string "Super Admin", so a renamed role keeps the lock.
   */
  const lastSuperAdminId = useMemo(() => {
    const supers = admins.filter((a) => a.is_super && a.status === 'ACTIVE');
    return supers.length === 1 ? (supers[0]?.id ?? null) : null;
  }, [admins]);
  const isLastSuper = selected !== null && selected.id === lastSuperAdminId;

  /** Where the chosen role would leave an admin who holds no second factor. */
  const roleNeedsFactor =
    action === 'role' &&
    selected !== null &&
    nextRole !== selected.role &&
    figures.twoFactorRoles.has(nextRole) &&
    !hasFactor(selected.two_factor);

  const openAction = useCallback(
    (kind: ActionKind) => {
      if (!selected) return;
      setAction(kind);
      setActionErrors({});
      setReasonCode(REASONS[kind][0]?.code ?? '');
      setNote('');
      setNextRole(selected.role);
      /* The scope dialog opens on what they HAVE, so an operator who only wants
         to add a brand does not accidentally revoke the rest. Brand NAMES come
         down on the roster, so they are mapped back to ids here. */
      setNextBrands(
        brands.filter((b) => selected.brands.includes(b.name)).map((b) => b.id),
      );
    },
    [selected, brands],
  );

  /**
   * Post one of the member writes.
   *
   * What this form can know is checked before anything is sent — the route
   * re-checks every one of them, because a browser is not a guard — and a field
   * problem stays beside its field rather than opening a modal the operator has
   * to dismiss before they can see which box was wrong. A FAILURE opens the
   * dialog: a write that did not happen must not scroll away in a toast.
   */
  const submitAction = useCallback(async () => {
    if (!selected || !action) return;

    const errors: Record<string, string> = {};
    if (note.trim().length < NOTE_MINIMUM) {
      errors['note'] = `Say why, in at least ${NOTE_MINIMUM} characters — a code alone reads as a form number.`;
    }
    if (action === 'role') {
      if (nextRole === selected.role) {
        errors['roleName'] = `${selected.display_name} already holds ${selected.role}.`;
      } else if (roleNeedsFactor) {
        errors['roleName'] =
          `${nextRole} carries a capability the platform only honours from a session verified with two-factor authentication, and ${selected.display_name} has not enrolled one. Enrol the factor first, or choose a role without it.`;
      }
    }
    if (action === 'scope') {
      const held = brands.filter((b) => selected.brands.includes(b.name)).map((b) => b.id);
      const same =
        held.length === nextBrands.length && held.every((id) => nextBrands.includes(id));
      if (same) {
        errors['brandIds'] =
          nextBrands.length === 0
            ? `${selected.display_name} already sees every brand.`
            : 'That is the scope they already have. Change the selection, or cancel.';
      }
    }
    setActionErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSaving(true);
    try {
      const payload =
        action === 'role'
          ? { roleName: nextRole, reasonCode, note: note.trim() }
          : action === 'scope'
            ? { brandIds: nextBrands, reasonCode, note: note.trim() }
            : { reasonCode, note: note.trim() };

      await api.post(`/api/access/admins/${selected.id}/${ACTION_PATH[action]}`, payload);

      toast(
        action === 'role'
          ? `${selected.display_name} now holds ${nextRole}. It binds on their next request — no re-login.`
          : action === 'suspend'
            ? `${selected.display_name} is suspended, and every session they held has ended.`
            : action === 'reinstate'
              ? `${selected.display_name} is active again. They have to sign in afresh.`
              : action === 'scope'
                ? `${selected.display_name} now sees ${nextBrands.length === 0 ? 'every brand' : `${formatCount(nextBrands.length)} brand${nextBrands.length === 1 ? '' : 's'}`}. It binds on their next request.`
                : action === 'reset2fa'
                  ? `${selected.display_name}'s authenticator is cleared and their sessions have ended. Nothing was sent: they enrol a new one themselves at their next sign-in.`
                  : `Every session ${selected.display_name} held has ended. Their account is untouched — they can sign straight back in.`,
      );
      setAction(null);
      setNote('');
      setWriteCount((n) => n + 1);
    } catch (error: unknown) {
      // A refusal the route computed and this form could not: the last Super
      // Admin, a state that moved under us, a brand that no longer exists.
      if (error instanceof ApiRequestError && error.fields) setActionErrors(error.fields);
      showError({
        title: `${ACTION_TITLE[action]} — nothing changed`,
        message:
          error instanceof ApiRequestError
            ? error.message
            : `${selected.display_name}'s account is exactly as it was. Try again shortly.`,
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setSaving(false);
    }
  }, [selected, action, note, reasonCode, nextRole, nextBrands, brands, roleNeedsFactor, toast, showError]);

  /** Create the invited admin. Writes an INVITED row — and sends nothing. */
  const submitInvite = useCallback(async () => {
    const name = invite.name.trim();
    const email = invite.email.trim();
    const errors: Record<string, string> = {};
    if (name.length < 2) errors['displayName'] = 'Give their full name as it will appear on the roster.';
    // Deliberately loose: the route parses the address with the same schema the
    // rest of the platform uses, and a browser regex that disagreed with it
    // would reject addresses the platform accepts.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors['email'] = 'Enter a valid work email address.';
    if (!invite.role) errors['roleName'] = 'Choose the role this admin will hold.';
    setInviteErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSaving(true);
    try {
      const created = await api.post<{ inviteExpiresAt: string }>('/api/access/admins', {
        email,
        displayName: name,
        roleName: invite.role,
        // '' is All brands, which is the ABSENCE of a scope row, not a brand.
        brandIds: invite.scope ? [invite.scope] : [],
      });
      /* Says what happened, not what an operator might assume happened: the row
         is on the roster, nothing left the platform, and the next step is
         theirs. */
      toast(
        `${name} invited as ${invite.role} — the row is on the roster now. Nothing was emailed ` +
          `yet: the invite message carries the acceptance link, and no link exists until you ` +
          `press Invite link on their row. Do that next. The invite lapses ` +
          `${stamp(created.inviteExpiresAt)}.`,
      );
      setInviteOpen(false);
      setInvite({ name: '', email: '', role: '', scope: '', twofa: 'Authenticator app' });
      setInviteErrors({});
      setWriteCount((n) => n + 1);
    } catch (error: unknown) {
      if (error instanceof ApiRequestError && error.fields) setInviteErrors(error.fields);
      showError({
        title: 'No admin was created',
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'No account was created. Try again shortly.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setSaving(false);
    }
  }, [invite, toast, showError]);

  /** Generate the acceptance link. Shows it once; sends nothing. */
  const requestInviteLink = useCallback(async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const link = await api.post<InviteLink>(
        `/api/access/admins/${selected.id}/invite-link`,
        {},
      );
      setInviteLink(link);
      setWriteCount((n) => n + 1);
    } catch (error: unknown) {
      showError({
        title: 'No invite link was created',
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'Nothing was generated. Try again shortly.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setSaving(false);
    }
  }, [selected, showError]);

  /** Toggle one matrix cell. */
  const openCell = useCallback((role: RoleDef, permission: PermissionDef, grant: boolean) => {
    setCell({ role, permission, grant });
    setCellErrors({});
    setCellReason(MATRIX_REASONS[0]?.code ?? '');
    setCellNote('');
  }, []);

  const submitCell = useCallback(async () => {
    if (!cell) return;
    const errors: Record<string, string> = {};
    if (cellNote.trim().length < NOTE_MINIMUM) {
      errors['note'] = `Say why, in at least ${NOTE_MINIMUM} characters — this changes what a whole role may do.`;
    }
    setCellErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSaving(true);
    try {
      await api.post(`/api/access/roles/${cell.role.id}/permissions`, {
        permissionKey: cell.permission.key,
        grant: cell.grant,
        reasonCode: cellReason,
        note: cellNote.trim(),
      });
      toast(
        cell.grant
          ? `${cell.role.name} now holds ${cell.permission.label}. It binds on every holder's next request.`
          : `${cell.role.name} no longer holds ${cell.permission.label}. It binds on every holder's next request.`,
      );
      setCell(null);
      setWriteCount((n) => n + 1);
    } catch (error: unknown) {
      if (error instanceof ApiRequestError && error.fields) setCellErrors(error.fields);
      showError({
        title: `${cell.role.name} is unchanged`,
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'The capability was not moved. Try again shortly.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setSaving(false);
    }
  }, [cell, cellNote, cellReason, toast, showError]);

  // Nothing on this screen is readable without one of the two capabilities, so
  // the wall is stated once rather than as three empty tabs.
  if (!canManage && !canExport) {
    return (
      <EmptyState icon="lock-key" title="Security & access control">
        This module needs access.manage to read the roster and the permission matrix, or
        audit.export to read the sign-in trail. Your role holds neither.
      </EmptyState>
    );
  }

  const scopedBrand = brands.find((b) => b.id === brandId);
  /* The signed-in admin's OWN restriction, which is what an export is scoped
     by. `session.brands` is the restriction; empty means every brand. */
  const ownScopeNames = (session?.brands ?? []).map((b) => b.name);

  /*
   * ONE CAPTION, THREE STATES, AND THE MIDDLE ONE IS WHY IT IS WRITTEN LIKE
   * THIS.
   *
   * Each tab's caption has to say which of three things is true: the read
   * failed, the read is still running, or the read came back and these are the
   * figures. An operator who cannot tell a failed read from a genuinely quiet
   * period will take "0 sign-in attempts" to mean nobody tried, and on this
   * screen of all of them that is the wrong conclusion to hand somebody. So
   * each failure branch replaces the figures with a sentence saying they count
   * nothing, rather than printing a zero with a warning next to it.
   *
   * THE ROLES CAPTION USED TO END "live from identity.role_permission". The
   * name was accurate — the matrix reads and writes identity.role_permission
   * directly, and that is the table the session plugin selects a principal's
   * capabilities from on every request, which the module comment at the top of
   * this file records for whoever is reading the source. It was still the wrong
   * thing to put in front of a security lead, who cannot look a table name up,
   * ask anyone for it, or do anything with it. What they actually needed were
   * the two claims the clause was carrying: this grid is not a cached copy, and
   * it is the same record every permission check consults, so an edit made here
   * takes effect everywhere. The wording now says those two things and keeps
   * the identifier here in the comment.
   */
  const subtitle =
    tab === 'admins'
      ? rosterFailed
        ? 'The roster could not be read — no figure below counts anything'
        : rosterLoading
          ? 'Loading…'
          : `${formatCount(admins.length)} admin users · 2FA mandatory for any role holding money permissions`
      : tab === 'roles'
        ? matrixFailed
          ? 'The permission matrix could not be read — the grid below is withheld, not empty'
          : matrixLoading
            ? 'Loading…'
            : `${formatCount(matrix?.roles.length ?? 0)} roles × ${formatCount(matrix?.permissions.length ?? 0)} capabilities · read fresh each time, from the one record every permission check uses — a change here applies everywhere`
        : attemptsFailed
          ? 'The sign-in trail could not be read — this is not a quiet period'
          : attemptsLoading
            ? 'Loading…'
            : `${formatCount(attempts.length)} sign-in attempts, newest first · this is the authentication trail, not the privileged-action log`;

  // Spread rather than `count: … : undefined`: exactOptionalPropertyTypes is on,
  // and a tab whose data this role cannot read carries no badge at all rather
  // than a zero that would read as "nobody has signed in".
  const tabs: { key: TabKey; label: string; count?: number }[] = [
    {
      key: 'admins',
      label: 'Admin users',
      ...(canManage && !rosterFailed ? { count: admins.length } : {}),
    },
    { key: 'roles', label: 'Roles & permissions' },
    {
      key: 'audit',
      label: 'Audit log',
      ...(canExport && !attemptsFailed ? { count: attempts.length } : {}),
    },
  ];

  return (
    <>
      {/* The header goes on THIS return, not on the capability wall above it.
          The wall is a refusal — somebody who cannot read any of the three tabs
          is not standing on the screen and does not need its purpose explained
          to them; they need to know which capability they are missing, which is
          all the empty state says. `meta` carries the per-tab caption unchanged,
          so the failed/loading/counts distinction above still reaches the eye in
          the same place it always did. */}
      <ScreenHeader
        title="Security & access control"
        purpose="The security lead comes here to decide who may use the back office and what each role is allowed to do, and to see who has been trying to sign in."
        meta={subtitle}
        actions={
          <>
            <Button onClick={auditExport.start} disabled={!canExport}>
              <Icon name="export" />
              Export audit log
            </Button>
            <Button variant="primary" disabled={!canManage} onClick={() => setInviteOpen(true)}>
              <Icon name="user-plus" />
              Invite admin
            </Button>
          </>
        }
      />

      {/* "Actions this role cannot perform are locked, not hidden, so you can
          see where the wall is." Rendered from the real session role, and only
          when that role is not the super one. */}
      {session && !session.isSuper && (
        <div className="notice notice-quiet">
          <Icon name="shield-warning" />
          <div>
            Viewing as {session.role} — actions this role cannot perform are locked, not hidden, so
            you can see where the wall is.
          </div>
        </div>
      )}

      <Tabs tabs={tabs} active={tab} onChange={setTab} label="Access control sections" />

      {/* Each of these three tabs can refuse, and a refusal names the CAPABILITY the operator
          would have to be granted — never the route it would have been read from. A capability is
          something they can go and ask for, and it is on the matrix one tab over; a path is not
          something they can open, quote to anybody who will act on it, or do anything else with.
          The routes behind these three reads, and the capability each is gated on, are listed at
          the top of this file for whoever is reading the source. */}
      {tab === 'admins' &&
        (!canManage ? (
          <EmptyState icon="lock-key" title="The roster needs access.manage">
            Reading the roster needs the access.manage capability, which your role does not hold,
            so there is nothing to show you rather than a partial list. Who holds which role is
            itself privileged information — knowing which admin can release a withdrawal is the
            first step in targeting one.
          </EmptyState>
        ) : (
          <Roster
            admins={admins}
            visible={visible}
            matrix={matrix}
            figures={figures}
            query={query}
            onQuery={setQuery}
            roleFilter={roleFilter}
            onRoleFilter={setRoleFilter}
            loading={rosterLoading}
            failed={rosterFailed}
            matrixFailed={matrixFailed}
            matrixLoading={matrixLoading}
            onSelect={setSelected}
          />
        ))}

      {tab === 'roles' &&
        (!canManage ? (
          <EmptyState icon="lock-key" title="Read-only for your role">
            Editing roles requires access.manage, which only Super Admin holds. On this build even
            READING the matrix needs it — the same capability gates the read — so the grid is
            withheld rather than half-drawn.
          </EmptyState>
        ) : (
          <PermissionMatrix
            matrix={matrix}
            loading={matrixLoading}
            failed={matrixFailed}
            twoFactorRoles={figures.twoFactorRoles}
            onToggle={openCell}
          />
        ))}

      {tab === 'audit' &&
        (!canExport ? (
          <EmptyState icon="lock-key" title="The trail needs audit.export">
            Reading the sign-in trail needs the audit.export capability, which your role does not
            hold. The capability is deliberately separate from access.manage: reading who tried to
            sign in is a different privilege from changing who can.
          </EmptyState>
        ) : (
          <SignInTrail attempts={attempts} loading={attemptsLoading} failed={attemptsFailed} />
        ))}

      <NotYetAvailable scopedBrandName={scopedBrand?.name ?? null} />

      <AdminDrawer
        admin={selected}
        onClose={() => setSelected(null)}
        canManage={canManage}
        canExport={canExport}
        saving={saving}
        isSelf={isSelf}
        isLastSuper={isLastSuper}
        roleNeedsFactorRoles={figures.twoFactorRoles}
        history={history}
        attemptsFailed={attemptsFailed}
        sessions={sessions}
        sessionsLoading={sessionsLoading}
        sessionsFailed={sessionsFailed}
        actions={actorActions}
        actionsLoading={actionsLoading}
        actionsFailed={actionsFailed}
        onAction={openAction}
        onInviteLink={() => void requestInviteLink()}
      />

      <InviteDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        invite={invite}
        onInvite={setInvite}
        matrix={matrix}
        brands={brands}
        errors={inviteErrors}
        saving={saving}
        canManage={canManage}
        onSubmit={() => void submitInvite()}
      />

      <InviteLinkDialog link={inviteLink} onClose={() => setInviteLink(null)} />

      <ActionDialog
        action={action}
        admin={selected}
        matrix={matrix}
        brands={brands}
        saving={saving}
        errors={actionErrors}
        reasonCode={reasonCode}
        onReasonCode={setReasonCode}
        note={note}
        onNote={setNote}
        nextRole={nextRole}
        onNextRole={setNextRole}
        nextBrands={nextBrands}
        onNextBrands={setNextBrands}
        roleNeedsFactor={roleNeedsFactor}
        twoFactorRoles={figures.twoFactorRoles}
        onSubmit={() => void submitAction()}
        onClose={() => setAction(null)}
      />

      <MatrixCellDialog
        target={cell}
        saving={saving}
        errors={cellErrors}
        reasonCode={cellReason}
        onReasonCode={setCellReason}
        note={cellNote}
        onNote={setCellNote}
        onSubmit={() => void submitCell()}
        onClose={() => setCell(null)}
      />

      <ExportDialog
        open={auditExport.open}
        onClose={auditExport.close}
        from={auditExport.from}
        to={auditExport.to}
        onFrom={auditExport.setFrom}
        onTo={auditExport.setTo}
        errors={auditExport.errors}
        busy={auditExport.busy}
        outcome={auditExport.outcome}
        scopedBrandNames={ownScopeNames}
        onSubmit={auditExport.submit}
        onDownload={auditExport.download}
      />
    </>
  );
}
